import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { safeMarkdownProps } from '../lib/safeMarkdown';
import remarkGfm from 'remark-gfm';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBrain,
  IconCheck,
  IconExternalLink,
  IconRadar2,
  IconRefresh,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KianUsageLine, kianUsageFromCall } from './KianUsageLine';
import { api } from '../lib/api';
import type { DailyBriefing } from '../lib/api';
import { InjectionWarningAlert } from './KianAnalysisPanel';

/**
 * KIAN-Lagebericht.
 *
 * Aktualisiert wird NUR auf Knopfdruck — maximal 4 manuelle Berichte pro Tag; der
 * automatische 07:00-Bericht läuft zusätzlich und zählt nicht dagegen. Ohne
 * Ticket-Änderungen seit dem letzten Bericht ist der Knopf aus („Stand aktuell") —
 * ein identischer Zweitbericht würde nur Geld kosten.
 */

/**
 * Nackte Ticketnummern (T26-01234) in /t/-Deeplinks wandeln — bestehende
 * Markdown-Links bleiben unangetastet. Bewusst ohne Lookbehind (Safari-Altbestand).
 */
export function linkifyTicketNumbers(markdown: string): string {
  return markdown.replace(
    /\[[^\]]*\]\([^)]*\)|\b(T\d{2}-\d{3,6})\b/g,
    (match, bare: string | undefined) => (bare ? `[${bare}](/t/${bare})` : match)
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}


/** Reiner Renderer eines Berichts — auch für Archiv-Ansichten nutzbar. */
export function BriefingContent({ briefing }: { briefing: DailyBriefing }) {
  const navigate = useNavigate();
  const s = briefing.stats;

  return (
    <Stack gap="sm">
      {/* Chips verlinken in die Ticketübersicht mit passendem Filter — bewusst NEUER TAB,
          damit der Bericht als Arbeitsliste erhalten bleibt. */}
      <Group gap={6}>
        <Badge
          variant="light"
          color="orange"
          component="a"
          href="/tickets?tab=open"
          target="_blank"
          rel="noopener noreferrer"
          style={{ cursor: 'pointer' }}
        >
          {s.openTotal} offen
        </Badge>
        {typeof s.netLast24h === 'number' && (
          <Tooltip
            label={`${s.openedLast24h ?? 0} neu · ${s.closedLast24h ?? 0} erledigt in 24 h`}
            withArrow
          >
            <Badge
              variant="light"
              color={s.netLast24h > 0 ? 'red' : s.netLast24h < 0 ? 'teal' : 'gray'}
            >
              {s.netLast24h > 0 ? '+' : s.netLast24h < 0 ? '−' : '±'}
              {Math.abs(s.netLast24h)} netto (24 h)
            </Badge>
          </Tooltip>
        )}
        {s.unassigned > 0 && (
          <Badge
            variant="light"
            color="violet"
            component="a"
            href="/tickets?tab=unassigned"
            target="_blank"
            rel="noopener noreferrer"
            style={{ cursor: 'pointer' }}
          >
            {s.unassigned} unzugewiesen
          </Badge>
        )}
        {s.assignedUntouched > 0 && (
          <Badge
            variant="light"
            color="yellow"
            component="a"
            href="/tickets?tab=open&view=untouched"
            target="_blank"
            rel="noopener noreferrer"
            style={{ cursor: 'pointer' }}
          >
            {s.assignedUntouched} unberührt
          </Badge>
        )}
        <Badge
          variant="light"
          color="blue"
          component="a"
          href={`/tickets?tab=open&status=${encodeURIComponent('in bearbeitung')}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ cursor: 'pointer' }}
        >
          {s.inProgress} in Arbeit
        </Badge>
        {s.waiting > 0 && (
          <Badge
            variant="light"
            color="gray"
            component="a"
            href={`/tickets?tab=open&status=${encodeURIComponent('wartend')}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ cursor: 'pointer' }}
          >
            {s.waiting} wartend
          </Badge>
        )}
        {s.overdue > 0 && <Badge variant="filled" color="red">{s.overdue} überfällig</Badge>}
        {s.slaBreached > 0 && <Badge variant="filled" color="red">{s.slaBreached} SLA gerissen</Badge>}
        {s.customerMissing > 0 && (
          <Badge
            variant="outline"
            color="orange"
            component="a"
            href="/tickets?tab=no-customer"
            target="_blank"
            rel="noopener noreferrer"
            style={{ cursor: 'pointer' }}
          >
            {s.customerMissing} ohne Kunde
          </Badge>
        )}
        <Badge
          variant="light"
          color="green"
          leftSection={<IconCheck size={10} />}
          component="a"
          href={`/tickets?tab=all&status=${encodeURIComponent('geschlossen')}&within=48`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ cursor: 'pointer' }}
        >
          {s.closedLast48h} erledigt (48 h)
        </Badge>
      </Group>

      <InjectionWarningAlert
        blocked={s.injectionBlocked ?? 0}
        flagged={s.injectionFlagged ?? 0}
        subjectGenitive="dieses Berichts"
      />

      <Box style={{ fontSize: 'var(--mantine-font-size-sm)', lineHeight: 1.6 }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          {...safeMarkdownProps}
          components={{
            a: ({ href, children }) => {
              if (href?.startsWith('/t/')) {
                // Deeplink im Stil des orangen Ticket-Badges — bewusst NEUER TAB:
                // der Bericht ist die Arbeitsliste, sie soll beim Abarbeiten der
                // Tickets nicht durch Navigation verloren gehen.
                return (
                  <Anchor
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      color: 'var(--fb-orange, orange)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {children}
                  </Anchor>
                );
              }
              if (href?.startsWith('/')) {
                return (
                  <Anchor component="button" onClick={() => navigate(href)}>
                    {children}
                  </Anchor>
                );
              }
              return (
                <Anchor href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </Anchor>
              );
            },
            h2: ({ children }) => (
              <Text size="md" fw={700} mt={14} mb={4} c="violet.7">{children}</Text>
            ),
            h3: ({ children }) => <Text size="sm" fw={700} mt={8} mb={2}>{children}</Text>,
            p: ({ children }) => <Text size="sm" mb={6}>{children}</Text>,
            li: ({ children }) => (
              <Text component="li" size="sm" style={{ marginLeft: 16, marginBottom: 3 }}>
                {children}
              </Text>
            ),
            strong: ({ children }) => (
              <Text component="span" size="sm" fw={700}>{children}</Text>
            ),
          }}
        >
          {linkifyTicketNumbers(briefing.briefingText)}
        </ReactMarkdown>
      </Box>

      <Group gap="xs" justify="space-between" wrap="wrap">
        <Text size="xs" c="dimmed">
          Erstellt {formatDateTime(briefing.createdAt)}
          {briefing.autoTriggered
            ? ' · automatischer 07:00-Bericht'
            : ` · angefordert von ${briefing.triggeredByName ?? 'Unbekannt'}`}
          {s.truncatedFromLlm > 0
            ? ` · ${s.truncatedFromLlm} ruhige Tickets nur in den Kennzahlen`
            : ''}
        </Text>
        <KianUsageLine usage={kianUsageFromCall(s.llm)} style={{ cursor: 'help' }} />
      </Group>
    </Stack>
  );
}

export function DailyBriefingPanel({ compact = false }: { compact?: boolean }) {
  const queryClient = useQueryClient();

  const { data: status, isLoading } = useQuery({
    queryKey: ['briefing-status'],
    queryFn: () => api.getBriefingStatus(),
    staleTime: 30_000,
  });

  const generateMutation = useMutation({
    mutationFn: () => api.generateBriefing(),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['briefing-status'] });
    },
  });

  const briefing = generateMutation.data?.briefing ?? status?.latest ?? null;
  const isGenerating = generateMutation.isPending || (status?.generating ?? false);
  const noChanges = !!status?.latest && status.changesSinceLatest === 0;
  const limitReached = (status?.manualRemainingToday ?? 1) <= 0;
  const canGenerate = !isGenerating && !noChanges && !limitReached;

  // Wer heute schon manuell angefordert hat — im Tooltip für alle nachvollziehbar.
  const manualTodayList = (status?.manualToday ?? [])
    .map(
      (m) =>
        `${new Date(m.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} ${m.triggeredByName ?? 'Unbekannt'}`
    )
    .join(', ');
  const manualTodayHint = manualTodayList ? ` Heute angefordert: ${manualTodayList}.` : '';

  const buttonHint =
    (noChanges
      ? 'Keine Ticket-Änderungen seit dem letzten Bericht — er ist der aktuelle Stand.'
      : limitReached
        ? `Tageslimit erreicht (${status?.manualDailyLimit ?? 4} manuelle Berichte pro Tag). Der 07:00-Bericht kommt automatisch.`
        : `${status?.changesSinceLatest ?? 0} Ticket-Änderungen seit dem letzten Bericht.`) +
    manualTodayHint;

  const body = (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <IconRadar2 size={18} color="var(--mantine-color-violet-6)" />
          <Text fw={700} c="violet.7">
            KIAN-Lagebericht
          </Text>
        </Group>
        <Group gap="xs">
          {isGenerating && (
            <Group gap={6}>
              <Loader size="xs" color="violet" />
              <Text size="xs" c="dimmed">KIAN erstellt den Lagebericht…</Text>
            </Group>
          )}
          {!isGenerating && noChanges && (
            <Badge variant="light" color="green" leftSection={<IconCheck size={10} />}>
              Stand aktuell
            </Badge>
          )}
          {!isGenerating && status && (
            <Tooltip label={buttonHint} withArrow multiline w={280}>
              {/* span-Wrapper: Tooltips zeigen sich auch am deaktivierten Button */}
              <span>
                <Button
                  size="compact-sm"
                  variant="light"
                  color="violet"
                  leftSection={<IconRefresh size={13} />}
                  disabled={!canGenerate}
                  onClick={() => generateMutation.mutate()}
                >
                  Aktualisieren (noch {status.manualRemainingToday} von {status.manualDailyLimit})
                </Button>
              </span>
            </Tooltip>
          )}
          {compact && (
            <Button
              size="compact-xs"
              variant="subtle"
              component={RouterLink}
              to="/briefing?view=team"
              rightSection={<IconExternalLink size={12} />}
            >
              Vollansicht
            </Button>
          )}
        </Group>
      </Group>

      {isLoading && !briefing && (
        <Group gap="xs" justify="center" py="lg">
          <Loader size="sm" color="violet" />
        </Group>
      )}

      {generateMutation.isError && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
          <Text size="xs">
            Lagebericht konnte nicht erstellt werden:{' '}
            {generateMutation.error instanceof Error ? generateMutation.error.message : 'Fehler'}
            {briefing ? ' — unten steht der letzte verfügbare Stand.' : ''}
          </Text>
        </Alert>
      )}

      {briefing ? (
        compact ? (
          <ScrollArea.Autosize mah={420} type="auto">
            <BriefingContent briefing={briefing} />
          </ScrollArea.Autosize>
        ) : (
          <BriefingContent briefing={briefing} />
        )
      ) : (
        !isLoading &&
        !isGenerating && (
          <Alert color="gray" variant="light" p="xs" icon={<IconBrain size={16} />}>
            <Text size="xs" c="dimmed">
              Noch kein Lagebericht vorhanden — mit „Aktualisieren" jetzt erstellen. Ab morgen
              kommt um 07:00 automatisch einer.
            </Text>
          </Alert>
        )
      )}
    </Stack>
  );

  return (
    <Paper withBorder p="md" radius="md" style={{ borderColor: 'var(--mantine-color-violet-3)' }}>
      {body}
    </Paper>
  );
}
